import React, { useEffect, useState } from "react";
import { Button, Modal, ModalBody, Spinner } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import {
  loadCodelistsDetailId,
  Gender_OPTIONS,
  BloodGroup_OPTIONS,
  MedicalCode_OPTIONS,
  EducationLevel_OPTIONS,
  FoodHabit_OPTIONS,
  AlcoholResult_OPTIONS,
  SMOKING_OPTIONS,
} from "api/modules/common.option";
import { showToastMessage } from "common/service/CommonService";
import "../css/VolunteerFilterModal.scss";
import { parseLocalDate } from "helpers/constant";
import SimpleBar from "simplebar-react";
import { VolunteerFilterValues } from "../helper/volunteer.types";
import { customSelectStyles } from "../helper/volunteer.constant";



/* =========================================================
   Props
========================================================= */

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onApply: (filters: VolunteerFilterValues) => void;
  onClear: () => void;
  existingFilters?: VolunteerFilterValues;
}

/* =========================================================
   Component
========================================================= */

const VolunteerFilterModal: React.FC<Props> = ({
  isOpen,
  toggle,
  onApply,
  onClear,
  existingFilters,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useForm<VolunteerFilterValues>({
    defaultValues: existingFilters ?? {},
  });

  const screeningFromDateValue = watch("screeningFromDate");

  // Local state for options
  const [genderOptions, setGenderOptions] = useState<any[]>([]);
  const [bloodGroupOptions, setBloodGroupOptions] = useState<any[]>([]);
  const [medicalCodeOptions, setMedicalCodeOptions] = useState<any[]>([]);
  const [educationLevelOptions, setEducationLevelOptions] = useState<any[]>([]);
  const [foodHabitOptions, setFoodHabitOptions] = useState<any[]>([]);

  /* ---------------- LOAD CODELISTS ---------------- */
  useEffect(() => {
    const load = async () => {
      const result = await loadCodelistsDetailId([
        "Gender_OPTIONS",
        "BloodGroup_OPTIONS",
        "MedicalCode_OPTIONS",
        "IDPROOF_OPTIONS",
        "MaritalStatus_OPTIONS",
        "Ethnicity_OPTIONS",
        "Religion_OPTIONS",
        "Occupation_OPTIONS",
        "EducationLevel_OPTIONS",
        "PrimaryLanguage_OPTIONS",
        "FoodHabit_OPTIONS",
        "PopulationGroup_OPTIONS",
        "LiterateStatus_OPTIONS",
        "Race_OPTIONS",
        "AlcoholResult_OPTIONS",
        "SMOKING_OPTIONS",
        "Attachment_OPTIONS",
      ]);

      const allLoaded = Object.values(result).every((flag) => flag);
      if (allLoaded) {
        setGenderOptions([...Gender_OPTIONS]);
        setBloodGroupOptions([...BloodGroup_OPTIONS]);
        setMedicalCodeOptions([...MedicalCode_OPTIONS]);
        setEducationLevelOptions([...EducationLevel_OPTIONS]);
        setFoodHabitOptions([...FoodHabit_OPTIONS]);
      } else {
        showToastMessage("error", "Some dropdowns failed to load");
      }
    };
    if (isOpen) {
      load();
    }
  }, [isOpen]);

  /* ---------------- APPLY FILTER ---------------- */

  const onSubmit = (values: VolunteerFilterValues) => {
    const normalizedFilters: VolunteerFilterValues = {
      gender: values.gender || null,
      minAge: values.minAge ?? null,
      maxAge: values.maxAge ?? null,
      minbmi: values.minbmi ?? null,
      maxbmi: values.maxbmi ?? null,
      isSmoker: values.isSmoker || null,
      alcohol: values.alcohol || null,
      alcoholresult: values.alcoholresult || null,
      bloodgroup: values.bloodgroup || null,
      educationlevel: values.educationlevel || null,
      foodhabit: values.foodhabit || null,
      city: values.city?.trim() || null,
      screeningPassed: values.screeningPassed || null,
      screeningFromDate: values.screeningFromDate || null,
      screeningToDate: values.screeningToDate || null,
    };

    onApply(normalizedFilters);
    toggle();
  };

  /* ---------------- CLEAR FILTER ---------------- */

  const handleClear = () => {
    onClear();
    toggle();
    reset({
      gender: null,
      minAge: null,
      maxAge: null,
      minbmi: null,
      maxbmi: null,
      isSmoker: null,
      alcoholresult: null,
      bloodgroup: null,
      educationlevel: null,
      foodhabit: null,
      city: null,
      screeningPassed: null,
      screeningFromDate: null,
      screeningToDate: null,
    });
  };

  useEffect(() => {
    if (existingFilters && Object.keys(existingFilters).length > 0) {
      reset(existingFilters);
    } else {
      reset({
        gender: null,
        minAge: null,
        maxAge: null,
        minbmi: null,
        maxbmi: null,
        isSmoker: null,
        alcoholresult: null,
        bloodgroup: null,
        educationlevel: null,
        foodhabit: null,
        city: null,
        screeningPassed: null,
        screeningFromDate: null,
        screeningToDate: null,
      });
    }
  }, [existingFilters, reset]);



  /* ---------------- DATE FORMATTER ---------------- */
  const handleDateChange = (
    dates: Date[],
    onChange: (val: string | null) => void,
  ) => {
    if (dates && dates[0]) {
      const d = dates[0];
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      onChange(`${yyyy}-${mm}-${dd}`);
    } else {
      onChange(null);
    }
  };

  /* ---------------- RENDER ---------------- */

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      centered
      backdrop="static"
      className="volunteer-filter-modal common_modal"
    >
      <div className="modal-header">
        <h5 className="modal-title-custom">Filter Volunteers</h5>
        <p className="modal-subtitle">
          Apply specific criteria to narrow down your selection
        </p>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={toggle}
        ></button>
      </div>

      <ModalBody className="p-0">
        <SimpleBar className="p-4" style={{ maxHeight: "400px" }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* DEMOGRAPHICS */}
            <div className="filter-section">
              <h6 className="section-title">DEMOGRAPHICS</h6>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <div className="form-group mb-0">
                    <label>GENDER</label>
                    <Controller
                      name="gender"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={genderOptions}
                          styles={customSelectStyles}
                          placeholder="All Genders"
                          isClearable
                          value={
                            genderOptions.find(
                              (opt) => opt.value === field.value,
                            ) || null
                          }
                          onChange={(val: any) =>
                            field.onChange(val ? val.value : null)
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="col-md-8 mb-3">
                  <div className="form-group mb-0">
                    <label>AGE RANGE</label>
                    <div className="range-inputs">
                      <Controller
                        name="minAge"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="number"
                            className="form-control"
                            placeholder="Min Age"
                            value={field.value === null ? "" : field.value}
                          />
                        )}
                      />
                      <span className="range-separator">to</span>
                      <Controller
                        name="maxAge"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="number"
                            className="form-control"
                            placeholder="Max Age"
                            value={field.value === null ? "" : field.value}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <div className="form-group mb-0">
                    <label>EDUCATION LEVEL</label>
                    <Controller
                      name="educationlevel"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={educationLevelOptions}
                          styles={customSelectStyles}
                          placeholder="All Levels"
                          isClearable
                          value={
                            educationLevelOptions.find(
                              (opt) => opt.value === field.value,
                            ) || null
                          }
                          onChange={(val: any) =>
                            field.onChange(val ? val.value : null)
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="col-md-8 mb-3">
                  <div className="form-group mb-0">
                    <label>CITY / LOCATION</label>
                    <div className="position-relative">
                      <div className="input-group-text">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <Controller
                        name="city"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="text"
                            className="form-control input-with-icon"
                            placeholder="Search by city..."
                            value={field.value || ""}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* HEALTH & HABITS */}
            <div className="filter-section">
              <h6 className="section-title">HEALTH & HABITS</h6>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <div className="form-group mb-0">
                    <label>BMI RANGE</label>
                    <div className="range-inputs">
                      <Controller
                        name="minbmi"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="number"
                            className="form-control"
                            placeholder="Min"
                            value={field.value === null ? "" : field.value}
                          />
                        )}
                      />
                      <Controller
                        name="maxbmi"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="number"
                            className="form-control"
                            placeholder="Max"
                            value={field.value === null ? "" : field.value}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <div className="form-group mb-0">
                    <label>BLOOD GROUP</label>
                    <Controller
                      name="bloodgroup"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={bloodGroupOptions}
                          styles={customSelectStyles}
                          placeholder="Any Group"
                          isClearable
                          value={
                            bloodGroupOptions.find(
                              (opt) => opt.value === field.value,
                            ) || null
                          }
                          onChange={(val: any) =>
                            field.onChange(val ? val.value : null)
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <div className="form-group mb-0">
                    <label>FOOD HABIT</label>
                    <Controller
                      name="foodhabit"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={foodHabitOptions}
                          styles={customSelectStyles}
                          placeholder="Any"
                          isClearable
                          value={
                            foodHabitOptions.find(
                              (opt) => opt.value === field.value,
                            ) || null
                          }
                          onChange={(val: any) =>
                            field.onChange(val ? val.value : null)
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <div className="form-group mb-0">
                    <label>SMOKER</label>
                    <Controller
                      name="isSmoker"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={SMOKING_OPTIONS}
                          styles={customSelectStyles}
                          placeholder="Any"
                          isClearable
                          value={
                            SMOKING_OPTIONS.find(
                              (opt) => opt.value === field.value,
                            ) || null
                          }
                          onChange={(val: any) =>
                            field.onChange(val ? val.value : null)
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <div className="form-group mb-0">
                    <label>ALCOHOL</label>
                    <Controller
                      name="alcoholresult"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={AlcoholResult_OPTIONS}
                          styles={customSelectStyles}
                          placeholder="Any"
                          isClearable
                          value={
                            AlcoholResult_OPTIONS.find(
                              (opt) => opt.value === field.value,
                            ) || null
                          }
                          onChange={(val: any) =>
                            field.onChange(val ? val.value : null)
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* SCREENING LIFECYCLE */}
            <div className="filter-section mb-0">
              <h6 className="section-title">SCREENING LIFECYCLE</h6>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <div className="form-group mb-0">
                    <label>SCREENING PASSED</label>
                    <Controller
                      name="screeningPassed"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={medicalCodeOptions}
                          styles={customSelectStyles}
                          placeholder="All Statuses"
                          isClearable
                          value={
                            medicalCodeOptions.find(
                              (opt) => opt.value === field.value,
                            ) || null
                          }
                          onChange={(val: any) =>
                            field.onChange(val ? val.value : null)
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="col-md-8 mb-3">
                  <div className="form-group mb-0">
                    <label>SCREENING DATE RANGE</label>
                    <div className="range-inputs">
                      <Controller
                        name="screeningFromDate"
                        control={control}
                        render={({ field }) => (
                          <Flatpickr
                            className="form-control"
                            options={{ dateFormat: "d/m/Y" }}
                            placeholder="dd/mm/yyyy"
                            value={field.value ? (parseLocalDate(field.value) ?? undefined) : undefined}
                            onChange={(dates: Date[]) => {
                              handleDateChange(dates, field.onChange);
                              setValue("screeningToDate", null);
                            }}
                          />
                        )}
                      />
                      <span className="range-separator">to</span>
                      <Controller
                        name="screeningToDate"
                        control={control}
                        render={({ field }) => (
                          <Flatpickr
                            key={screeningFromDateValue || "any"}
                            className="form-control"
                            options={{
                              dateFormat: "d/m/Y",
                              minDate: screeningFromDateValue ? (parseLocalDate(screeningFromDateValue) ?? undefined) : undefined
                            }}
                            placeholder="dd/mm/yyyy"
                            value={field.value ? (parseLocalDate(field.value) ?? undefined) : undefined}
                            onChange={(dates) =>
                              handleDateChange(dates, field.onChange)
                            }
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </SimpleBar>
      </ModalBody>

      <div className="modal-footer-custom">
        <button type="button" className="btn-clear" onClick={handleClear}>
          <i className="ri-refresh-line"></i> Clear Filters
        </button>

        <div className="d-flex gap-2">
          <Button color="secondary" outline onClick={toggle} >
            Cancel
          </Button>

          <Button
            disabled={isSubmitting}
            onClick={handleSubmit(onSubmit)}
            outline
            color="primary"
          >
            {isSubmitting && <Spinner size="sm" className="me-2" />}
            Apply Filters
          </Button>
        </div>
      </div>
    </Modal >
  );
};

export default VolunteerFilterModal;
